import { z } from 'zod';

/**
 * @aiPurpose closed catalog of commercial-proposal ("КП") lifecycle states
 *   (#1194). A DRAFT is editable by its owner; PUBLISHED freezes the
 *   content inputs AND the calculated outputs into a snapshot and exposes
 *   a token-authorized public view.
 * @aiInvariant append-only; mirrors the Prisma `CommercialProposalStatus`
 *   enum 1:1 (kept in lockstep by `scripts/check-enum-sync.sh`). Display
 *   labels (Черновик / Опубликовано) live in the i18n layer, never here.
 */
export enum CommercialProposalStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}
export const CommercialProposalStatusSchema = z.nativeEnum(CommercialProposalStatus);

/**
 * @aiPurpose closed catalog of buyout payment schemes. The selected scheme
 *   determines the payment divisor applied to the rouble totals
 *   (cash/card/crypto -> 1; novat -> no-VAT divisor; vat -> VAT divisor).
 * @aiInvariant literals are the wire contract; the human badge/label
 *   (e.g. "Оплата: р/с без НДС + УПД") is resolved in the i18n / public
 *   projection layer, never persisted as the source of truth. Array ORDER
 *   is the editor radio-group display order.
 */
export const COMMERCIAL_PROPOSAL_PAYMENT_SCHEMES = [
  'vat',
  'novat',
  'cash',
  'crypto',
  'card',
] as const;
export type CommercialProposalPaymentScheme = (typeof COMMERCIAL_PROPOSAL_PAYMENT_SCHEMES)[number];
export const CommercialProposalPaymentSchemeSchema = z.enum(COMMERCIAL_PROPOSAL_PAYMENT_SCHEMES);

/**
 * @aiPurpose closed catalog of КП composition modes (#1410). An INTERNAL
 *   content/calc/projection input selecting which section(s) the proposal
 *   covers: BOTH (выкуп + логистика), BUYOUT-only, or LOGISTICS-only. Default
 *   BOTH preserves the legacy single-mode-less behaviour exactly.
 * @aiInvariant the literals are the persisted contract; the editor labels
 *   (Выкуп + Логистика / Выкуп / Логистика) live in the i18n layer, never
 *   here. Array ORDER is the editor radio-group display order. This mode is
 *   NEVER added to the public DTO — the projection BLANKS the hidden section
 *   instead of exposing the mode.
 */
export const COMMERCIAL_PROPOSAL_MODES = ['BOTH', 'BUYOUT', 'LOGISTICS'] as const;
export type CommercialProposalMode = (typeof COMMERCIAL_PROPOSAL_MODES)[number];
export const CommercialProposalModeSchema = z.enum(COMMERCIAL_PROPOSAL_MODES);

/**
 * @aiPurpose closed catalog of the logistics tariff basis (#1411): per kg or
 *   per m³. An INTERNAL content/calc input selecting which quantity the
 *   `tariffUsdPerKg` rate multiplies to compute freight cost.
 * @aiInvariant the literals are the persisted contract; default 'kg' keeps
 *   every stored/old proposal byte-identical. Array ORDER is the editor toggle
 *   display order. NEVER added to the public DTO.
 */
export const COMMERCIAL_PROPOSAL_TARIFF_UNITS = ['kg', 'm3'] as const;
export type CommercialProposalTariffUnit = (typeof COMMERCIAL_PROPOSAL_TARIFF_UNITS)[number];
export const CommercialProposalTariffUnitSchema = z.enum(COMMERCIAL_PROPOSAL_TARIFF_UNITS);

export const COMMERCIAL_PROPOSAL_PUBLIC_CODE_REGEX = /^KP-\d{5}$/;

/**
 * @aiPurpose closed catalog of public-view event kinds surfaced on the
 *   owner/Admin analytics read (#1195). Mirrors the backend Prisma
 *   `CommercialProposalPublicViewKind` enum 1:1. `HTML_VIEW` is the MVP
 *   signal (a customer opening the token-authorized sheet); `PDF_DOWNLOAD`
 *   is reserved for the later download surface (#1197).
 * @aiInvariant append-only; kept in lockstep with the Prisma enum by
 *   `scripts/check-enum-sync.sh`.
 */
export enum CommercialProposalPublicViewKind {
  HTML_VIEW = 'HTML_VIEW',
  PDF_DOWNLOAD = 'PDF_DOWNLOAD',
}
export const CommercialProposalPublicViewKindSchema = z.nativeEnum(
  CommercialProposalPublicViewKind,
);
